// Labor cells are Bryntum HTML (itemTpl + chip view), not React: their fit
// (chipOverflow.js: full chips, then "+N") runs once per animation frame for every
// labor cell rendered since the last one, as one write / read / write pass so
// the whole batch costs a single layout.
import { escapeHtml } from "../../../../utils/escapeHtml";
import { fitChips, measureChip, MORE_CHIP_RESERVE_PX } from "./chipOverflow";

const VIEW_SELECTOR = ".offolio-labor-chipview";
const HIDDEN_CLASS = "offolio-labor-chip-hidden";
const MORE_CLASS = "offolio-resource-chip-overflow";
const LEVEL_CLASSES = ["offolio-chip-row--fit-1"];

const pending = new Set();
let frame = null;

// Bryntum does not re-render a row when its column is resized: one observer
// re-fits the labor cells whose width changed. Rows are recycled, so the
// observed set stays bounded by the rendered rows.
const observedWidth = new WeakMap();
let resizeObserver = null;
const observe = (cell) => {
  if (observedWidth.has(cell) || typeof ResizeObserver === "undefined") return;
  if (!resizeObserver) {
    resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(({ target, contentRect }) => {
        const width = Math.round(contentRect.width);
        if (observedWidth.get(target) === width) return;
        observedWidth.set(target, width);
        if (target.isConnected) scheduleLaborChipFit(target);
        else {
          resizeObserver.unobserve(target);
          observedWidth.delete(target);
        }
      });
    });
  }
  observedWidth.set(cell, null);
  resizeObserver.observe(cell);
};

const chipsOf = (view) =>
  [...view.children].filter((item) => item.querySelector?.(".offolio-labor-chip"));

const reset = (cell, view) => {
  view.classList.remove(...LEVEL_CLASSES);
  chipsOf(view).forEach((item) => item.classList.remove(HIDDEN_CLASS));
  cell.querySelectorAll(`.${MORE_CLASS}`).forEach((more) => more.remove());
};

const numberOr = (value, fallback) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

// Room for the chips: the cell's content box (the chip wrap is sized to its
// content, so its own width says nothing), minus the "+" and the chip view's
// padding (room for the focus ring and the remove control).
const measure = (cell, view, styleOf) => {
  const wrap = view.parentElement;
  const add = wrap?.querySelector(":scope > .offolio-labor-add");
  const cellStyle = styleOf(cell);
  const wrapGap = numberOr(wrap ? styleOf(wrap).columnGap : 0, 0);
  const viewStyle = styleOf(view);
  const gap = numberOr(viewStyle.columnGap, 0);
  const viewPadding = numberOr(viewStyle.paddingLeft, 0) + numberOr(viewStyle.paddingRight, 0);
  const cellPadding = numberOr(cellStyle.paddingLeft, 0) + numberOr(cellStyle.paddingRight, 0);
  const available =
    (cell.clientWidth || 0) - cellPadding - viewPadding - (add ? add.offsetWidth + wrapGap : 0);
  const items = chipsOf(view);
  return { items, widths: items.map((item) => measureChip(item)), available, gap, wrapGap };
};

const apply = (cell, view, { items, widths, available, gap, wrapGap }) => {
  // The view's right padding and the wrap gap (both already out of
  // `available` or added here) separate the last chip from the "+N".
  const { count, level } = fitChips(widths, available, { gap, reserve: MORE_CHIP_RESERVE_PX + wrapGap, moreGap: 0 });
  if (level > 0) view.classList.add(LEVEL_CLASSES[level - 1]);
  const hidden = items.slice(count);
  if (!hidden.length) return;
  hidden.forEach((item) => item.classList.add(HIDDEN_CLASS));
  const tip = hidden
    .map((item) => item.querySelector(".offolio-labor-chip")?.getAttribute("data-btip"))
    .filter(Boolean)
    .map(escapeHtml)
    .join("<br>");
  const chip = hidden[0].querySelector(".offolio-labor-chip");
  const taskId = (chip?.dataset.testid || "").replace(/^gantt-labor-chip-/, "").replace(/-[^-]+$/, "");
  view.insertAdjacentHTML(
    "afterend",
    `<span class="${MORE_CLASS} offolio-resource-chip-more" data-testid="gantt-labor-avatar-overflow-${escapeHtml(taskId)}" data-btip="${escapeHtml(tip)}">+${hidden.length}</span>`
  );
};

const run = () => {
  frame = null;
  const jobs = [];
  pending.forEach((cell) => {
    const view = cell.isConnected ? cell.querySelector(VIEW_SELECTOR) : null;
    if (!view) return;
    reset(cell, view);
    jobs.push({ cell, view });
  });
  pending.clear();
  // The cells of a column share their paddings and gaps: one style read per
  // class list and batch, not three per cell (a scroll renders dozens).
  const styles = new Map();
  const styleOf = (el) => {
    const k = `${el.tagName}.${el.className}`;
    if (!styles.has(k)) {
      const { columnGap, paddingLeft, paddingRight } = getComputedStyle(el);
      styles.set(k, { columnGap, paddingLeft, paddingRight });
    }
    return styles.get(k);
  };
  jobs.forEach((job) => { job.measured = measure(job.cell, job.view, styleOf); });
  jobs.forEach((job) => apply(job.cell, job.view, job.measured));
};

export const scheduleLaborChipFit = (cellElement) => {
  if (!cellElement) return;
  observe(cellElement);
  pending.add(cellElement);
  if (frame == null && typeof window !== "undefined" && window.requestAnimationFrame) {
    frame = window.requestAnimationFrame(run);
  }
};

// Tests: run the pending pass now.
export const flushLaborChipFit = () => {
  if (frame != null) window.cancelAnimationFrame?.(frame);
  run();
};

